import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const ViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const leaveTypeId = useMemo(
    () => location.state?.leaveTypeId || sessionStorage.getItem("selectedLeaveTypeId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_leave_type_details_${leaveTypeId || "unknown"}`, [leaveTypeId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { leaveType: null }), [cacheKey]);

  const [leaveType, setLeaveType] = useState(cachedData?.leaveType || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.leaveType);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [leaveTypeName, setLeaveTypeName] = useState("");
  const [leaveTypeDescription, setLeaveTypeDescription] = useState("");
  const [numberOfDays, setNumberOfDays] = useState("");
  const [durationUnit, setDurationUnit] = useState("DAYS");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadLeaveType = useCallback(async ({ silent = false } = {}) => {
      if (!apiBaseUrl || !leaveTypeId) {
        setErrorMessage(!leaveTypeId ? "Select a leave type from the list first." : "API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/leave-types/${leaveTypeId}`);
        const loadedLeaveType = response?.data?.leaveType || null;

        setLeaveType(loadedLeaveType);
        setLeaveTypeName(loadedLeaveType?.leaveTypeName || "");
        setLeaveTypeDescription(loadedLeaveType?.leaveTypeDescription || "");
        const loadedUnit = loadedLeaveType?.durationUnit === "WEEKS" ? "WEEKS" : "DAYS";
        setDurationUnit(loadedUnit);
        setNumberOfDays(String(loadedUnit === "WEEKS"
          ? (loadedLeaveType?.numberOfDays ?? 0) / 7
          : loadedLeaveType?.numberOfDays ?? ""));
        writeSessionCache(cacheKey, { leaveType: loadedLeaveType });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load leave type details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    }, [apiBaseUrl, cacheKey, leaveTypeId]);

  useEffect(() => {
    loadLeaveType({ silent: true });
  }, [loadLeaveType]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadLeaveType({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadLeaveType]);

  const refreshLeaveType = async () => {
    if (!apiBaseUrl || !leaveTypeId) {
      return;
    }

    const response = await axios.get(`${apiBaseUrl}/organizations/leave-types/${leaveTypeId}`);
    const loadedLeaveType = response?.data?.leaveType || null;

    setLeaveType(loadedLeaveType);
    setLeaveTypeName(loadedLeaveType?.leaveTypeName || "");
    setLeaveTypeDescription(loadedLeaveType?.leaveTypeDescription || "");
    const loadedUnit = loadedLeaveType?.durationUnit === "WEEKS" ? "WEEKS" : "DAYS";
    setDurationUnit(loadedUnit);
    setNumberOfDays(String(loadedUnit === "WEEKS"
      ? (loadedLeaveType?.numberOfDays ?? 0) / 7
      : loadedLeaveType?.numberOfDays ?? ""));
  };

  const openDeleteLeaveType = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const handleOpenEditModal = () => {
    setFormErrorMessage("");
    setOpenEditModal(true);
  };

  const handleUpdateLeaveType = async (ev) => {
    ev.preventDefault();

    if (!leaveTypeId || !apiBaseUrl) {
      setFormErrorMessage("Unable to update this leave type right now.");
      return;
    }

    const parsedDuration = Number(numberOfDays);

    if (!leaveTypeName.trim() || !leaveTypeDescription.trim() || !numberOfDays.trim()) {
      setFormErrorMessage("Leave type name, description and duration are required.");
      return;
    }

    if (!Number.isInteger(parsedDuration) || parsedDuration < 1) {
      setFormErrorMessage("Duration must be a whole number greater than 0.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/leave-types/${leaveTypeId}`, {
        leaveTypeName: leaveTypeName.trim(),
        leaveTypeDescription: leaveTypeDescription.trim(),
        numberOfDays: durationUnit === "WEEKS" ? parsedDuration * 7 : parsedDuration,
        durationUnit,
      });

      await refreshLeaveType();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update leave type. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLeaveType = async () => {
    if (!leaveTypeId || !apiBaseUrl) {
      setDeleteErrorMessage("Unable to delete this leave type right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/leave-types/${leaveTypeId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedLeaveTypeId");

      setTimeout(() => {
        navigate("/company-settings/leave-types");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete leave type. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = leaveType?.leaveTypeName || "Leave Type";
  const descriptionText =
    leaveType?.leaveTypeDescription || "No leave type description available yet.";
  const createdAt = leaveType?.createdAt
    ? new Date(leaveType.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";
  const savedDurationUnit = leaveType?.durationUnit === "WEEKS" ? "WEEKS" : "DAYS";
  const savedDurationValue = savedDurationUnit === "WEEKS"
    ? (leaveType?.numberOfDays ?? 0) / 7
    : leaveType?.numberOfDays;
  const savedDurationLabel = `${savedDurationValue ?? "-"} ${
    savedDurationUnit === "WEEKS"
      ? (savedDurationValue === 1 ? "week" : "weeks")
      : (savedDurationValue === 1 ? "day" : "days")
  }`;

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Leave Types", path: "/company-settings/leave-types" },
          { label: title },
        ]}
        title={title}
        subtitle="Leave type details and management actions."
        fallbackPath="/company-settings/leave-types"
        backLabel="Back to Leave Types"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenEditModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeleteLeaveType}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !leaveType ? (
        <FetchLoadingState message="Loading leave type details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !leaveType ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && leaveType ? (
        <InfoCard
          details="Leave Type Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Duration", value: savedDurationLabel },
            { label: "Leave Type ID", value: leaveType?.id || "-" },
          ]}
        />
      ) : null}

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={closeDeleteModal}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete leave type"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this leave type?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={closeDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteLeaveType}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="leave type deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">Leave type deleted</h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The leave type was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">Edit Leave Type</p>
          </div>
          <form
            className="my-12"
            onSubmit={handleUpdateLeaveType}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="leave-type-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Leave Type Name
              </label>
              <input
                type="text"
                id="leave-type-name"
                value={leaveTypeName}
                onChange={(ev) => setLeaveTypeName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="leave-type-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Description
              </label>
              <textarea
                id="leave-type-description"
                value={leaveTypeDescription}
                onChange={(ev) => setLeaveTypeDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-24 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="leave-duration"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Duration
              </label>
              <div className="mb-4 grid grid-cols-[1fr_140px] gap-3">
                <input
                  type="number"
                  id="leave-duration"
                  value={numberOfDays}
                  onChange={(ev) => setNumberOfDays(ev.target.value)}
                  min="1"
                  step="1"
                  className="w-full p-2 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
                <select
                  value={durationUnit}
                  onChange={(ev) => setDurationUnit(ev.target.value)}
                  aria-label="Leave duration unit"
                  className="w-full rounded-xl border-2 border-[#F3F5F5] bg-white px-3 focus:outline-[#1234CD]"
                >
                  <option value="DAYS">Days</option>
                  <option value="WEEKS">Weeks</option>
                </select>
              </div>
            </div>

            <div className="flex justify-center gap-12 mt-20 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />
    </div>
  );
};

export default ViewDetails;
